import { useState, useEffect, useMemo, useRef } from 'react'
import { ChevronDown, Printer, Users, RefreshCw, Split, X as XIcon } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { lotesService } from '../../services/lotesService'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { useLabelCurral } from '../../hooks/useLabelCurral'
import { downloadPdf } from '../../utils/pdfUtils'
import SepararFaturaModal from '../../components/modals/SepararFaturaModal'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')

const kgParaArrobas = (kg) => {
  if (!kg) return null
  return parseFloat(kg) / 30
}
const fmtArroba = (v) =>
  v != null ? v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' @' : '—'
const fmtKg = (v) =>
  v ? `${parseFloat(v).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 1 })} kg` : '—'
const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtDataIso = s => { if (!s) return ''; const [y, m, d] = s.split('T')[0].split('-'); return `${d}/${m}/${y}` }
const fmtDate = s => { if (!s) return ''; try { return new Date(s).toLocaleDateString('pt-BR') } catch { return s } }

// ─── Separação da lista (por tipo / sexo / fazenda) — só apresentação ─────────
const SEP_VAZIO = '(SEM INFORMAÇÃO)'
const SEXO_LABEL = { M: 'Macho', F: 'Fêmea', C: 'Castrado' }
const valorSep = (lote, criterio) => {
  const cavalo = !lote.tipo_animal && (lote.animais || []).length > 0
  let v = ''
  if (criterio === 'tipo') v = lote.tipo_animal?.nome || (cavalo ? 'Equino' : '')
  else if (criterio === 'sexo') v = lote.sexo || (cavalo ? (lote.animais.length === 1 ? (SEXO_LABEL[lote.animais[0].sexo] || '') : 'Misto') : '')
  else v = lote.fazenda?.nome || lote.fazenda_ficticia || ''
  return (v || '').trim().toUpperCase() || SEP_VAZIO
}
const opcoesSeparacao = (lotes) => {
  const out = {}
  for (const crit of ['tipo', 'sexo', 'fazenda']) {
    const m = {}
    lotes.forEach(l => {
      const v = valorSep(l, crit)
      const e = (m[v] = m[v] || { valor: v, lotes: 0, animais: 0, valor_total: 0, despesas: 0 })
      e.lotes += 1
      e.animais += parseInt(l.qtd) || 0
    })
    out[crit] = Object.values(m).sort((a, b) => b.animais - a.animais)
  }
  return out
}
const separarLotes = (lotes, cfg) => {
  if (!cfg?.criterio) return [{ nome: '', lotes }]
  const grupos = []
  const mapa = {}
  ;(cfg.grupos || []).forEach(g => {
    const valores = (g.valores || []).map(v => String(v).trim().toUpperCase()).filter(Boolean)
    if (!valores.length) return
    const idx = grupos.length
    grupos.push({ nome: (g.nome || '').trim(), lotes: [] })
    valores.forEach(v => { if (!(v in mapa)) mapa[v] = idx })
  })
  const demais = { nome: 'Demais', lotes: [] }
  lotes.forEach(l => {
    const idx = mapa[valorSep(l, cfg.criterio)]
    ;(idx !== undefined ? grupos[idx] : demais).lotes.push(l)
  })
  if (demais.lotes.length) grupos.push(demais)
  const ativos = grupos.filter(g => g.lotes.length)
  if (ativos.length <= 1 && !(ativos.length && ativos[0].nome)) return [{ nome: '', lotes }]
  return ativos.map((g, i) => {
    const fazenda = cfg.criterio === 'fazenda' ? (g.lotes[0]?.fazenda?.nome || g.lotes[0]?.fazenda_ficticia || '') : ''
    // O cabeçalho já mostra a fazenda: não repete o nome do grupo se for o mesmo.
    const nome = fazenda && g.nome.toLowerCase() === fazenda.trim().toLowerCase() ? '' : g.nome
    return { ...g, titulo: `Parte ${i + 1}/${ativos.length}` + (nome ? ` - ${nome}` : '') }
  })
}
const chaveSep = (l, v) => `lista-vendedores-sep:${l}:${v}`
const lerSep = (l, v) => { try { const r = localStorage.getItem(chaveSep(l, v)); return r ? JSON.parse(r) : null } catch { return null } }
const salvarSep = (l, v, cfg) => {
  try { cfg ? localStorage.setItem(chaveSep(l, v), JSON.stringify(cfg)) : localStorage.removeItem(chaveSep(l, v)) } catch { /* ignora */ }
}

// ─── Select ───────────────────────────────────────────────────────────────────
function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
        >
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

// ─── Estilos inline ──────────────────────────────────────────────────────────
const S = {
  page: {
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: 14,
    color: '#000',
    background: '#fff',
    padding: '20px 28px 24px',
  },
  hdr: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    borderBottom: '2px solid #1f2937',
    paddingBottom: 14,
    marginBottom: 18,
  },
  hdrLogo: { maxHeight: 80, maxWidth: 180, objectFit: 'contain' },
  hdrCenter: { flex: 1, textAlign: 'center', padding: '0 16px' },
  hdrT1: { fontSize: 20, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' },
  hdrT2: { fontSize: 14, fontWeight: 600, color: '#374151', marginTop: 4 },
  hdrT3: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  vendBlock: { marginBottom: 22, pageBreakInside: 'avoid' },
  vendHdr: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: '#f3f4f6', border: '1px solid #d1d5db', padding: '10px 16px', borderRadius: '4px 4px 0 0',
  },
  vendNome: { fontWeight: 700, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.03em', color: '#1f2937' },
  vendFaz: { fontSize: 12, color: '#374151', marginLeft: 8 },
  vendLotes: { fontSize: 12, color: '#374151', fontWeight: 600 },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { background: '#f3f4f6', border: '1px solid #d1d5db', padding: '7px 10px', textAlign: 'center', fontWeight: 700, fontSize: 12 },
  thL: { background: '#f3f4f6', border: '1px solid #d1d5db', padding: '7px 10px', textAlign: 'left', fontWeight: 700, fontSize: 12 },
  thR: { background: '#f3f4f6', border: '1px solid #d1d5db', padding: '7px 10px', textAlign: 'right', fontWeight: 700, fontSize: 12 },
  td: { border: '1px solid #e5e7eb', padding: '6px 10px', verticalAlign: 'middle' },
  tdC: { border: '1px solid #e5e7eb', padding: '6px 10px', textAlign: 'center', verticalAlign: 'middle' },
  tdR: { border: '1px solid #e5e7eb', padding: '6px 10px', textAlign: 'right', verticalAlign: 'middle' },
  totRow: { background: '#e5e7eb', fontWeight: 700 },
  totalGeral: {
    marginTop: 22, paddingTop: 14, borderTop: '2px solid #1f2937',
  },
  tgHdr: {
    background: '#f3f4f6', border: '1px solid #d1d5db', padding: '10px 16px', textAlign: 'center',
    fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#1f2937',
  },
  footer: { textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 18, borderTop: '1px solid #e5e7eb', paddingTop: 10 },
  totTbl: { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 10 },
  totThL: { background: '#D0D0D0', border: '1px solid #000', padding: '4px 6px', textAlign: 'left', fontWeight: 700, fontSize: 11 },
  totThC: { background: '#D0D0D0', border: '1px solid #000', padding: '4px 6px', textAlign: 'center', fontWeight: 700, fontSize: 11 },
  totTd:  { border: '1px solid #000', padding: '4px 6px', textAlign: 'left', fontSize: 12 },
  totTdC: { border: '1px solid #000', padding: '4px 6px', textAlign: 'center', fontSize: 12 },
  totTotRow: { border: '1px solid #000', padding: '4px 6px', fontSize: 12, fontWeight: 700 },
}

// ─── Tabela de um vendedor ────────────────────────────────────────────────────
function TabelaVendedor({ vendedor, lotes, colunasAtivas, titulo }) {
  const labelCurral = useLabelCurral()
  const totalAnimais = lotes.reduce((s, l) => s + (parseInt(l.qtd) || 0), 0)

  // Definição de colunas com render
  const COLUNAS = {
    lote:   { label: 'Lote',       width: '7%',  align: 'center', render: (l) => l.numero_lote },
    qtd:    { label: 'Qtd',        width: '6%',  align: 'center', render: (l) => l.qtd },
    tipo:   { label: 'Tipo',       width: '15%', align: 'center',   render: (l) => l.tipo_animal?.nome || '—' },
    raca:   { label: 'Raça',       width: '12%', align: 'center',   render: (l) => l.raca?.nome || '—' },
    sexo:   { label: 'Sexo',       width: '8%',  align: 'center', render: (l) => l.sexo || '—' },
    idade:  { label: 'Idade',      width: '10%', align: 'center',   render: (l) => l.idade?.nome || '—' },
    curral: { label: labelCurral,   width: '8%',  align: 'center', render: (l) => l.curral || '—' },
    peso:   { label: 'Peso Médio', width: '12%', align: 'center',  render: (l) => fmtKg(l.peso) },
    arroba: { label: 'Total @',    width: '10%', align: 'center',  render: (l) => fmtArroba(kgParaArrobas(l.peso)) },
  }

  const cols = colunasAtivas.filter(c => COLUNAS[c])

  const getThStyle = (col) => {
    const align = COLUNAS[col].align
    const base = align === 'right' ? S.thR : align === 'center' ? S.th : S.thL
    return { ...base, width: COLUNAS[col].width }
  }

  const getTdStyle = (col) => {
    const align = COLUNAS[col].align
    return align === 'right' ? S.tdR : align === 'center' ? S.tdC : S.td
  }

  return (
    <div style={S.vendBlock}>
      <div style={S.vendHdr}>
        <div>
          <span style={S.vendNome}>{vendedor.nome}</span>
          {titulo && <span style={S.vendFaz}> — <strong>{titulo}</strong></span>}
          {vendedor.fazenda && <span style={S.vendFaz}>· {/^faz/i.test(vendedor.fazenda.trim()) ? '' : 'Faz. '}{vendedor.fazenda}</span>}
          {vendedor.fazenda_cidade && (
            <span style={S.vendFaz}> — {vendedor.fazenda_cidade}{vendedor.fazenda_uf ? `/${vendedor.fazenda_uf}` : ''}</span>
          )}
        </div>
        <span style={S.vendLotes}>{lotes.length} lote(s)</span>
      </div>

      <table style={S.tbl}>
        <thead>
          <tr>
            {cols.map(col => (
              <th key={col} style={getThStyle(col)}>{COLUNAS[col].label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lotes.map((lote, idx) => (
            <tr key={lote.id} style={{ background: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
              {cols.map(col => (
                <td key={col} style={col === 'lote' ? { ...getTdStyle(col), fontWeight: 700 } : getTdStyle(col)}>
                  {COLUNAS[col].render(lote)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={S.totRow}>
            {cols.map((col, idx) => {
              if (col === 'lote') return <td key={col} style={{ ...S.tdC, fontSize: 11 }}>Lotes: {lotes.length}</td>
              if (col === 'qtd') return <td key={col} style={S.tdC}>{totalAnimais}</td>
              return <td key={col} style={S.td}></td>
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function ListaVendedores() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id
  const labelCurral = useLabelCurral()

  const [leiloes,    setLeiloes]    = useState([])
  const [vendedores, setVendedores] = useState([])
  const [leilaoId,   setLeilaoId]   = useState('')
  const [vendedorId, setVendedorId] = useState('')
  const [lotes,      setLotes]      = useState([])
  const [loading,    setLoading]    = useState(false)
  const [loadingL,   setLoadingL]   = useState(true)
  const [loadingV,   setLoadingV]   = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl,     setPdfUrl]     = useState(null)
  const [showPdf,    setShowPdf]    = useState(false)
  const [modelo,     setModelo]     = useState('detalhado') // 'detalhado', 'lotes' ou 'listagem'
  const [vendedorPor, setVendedorPor] = useState('fazenda')
  const [dadosListagem, setDadosListagem] = useState(null)
  const [loadingListagem, setLoadingListagem] = useState(false)
  const [reportConfig, setReportConfig] = useState(null)
  const [separacao, setSeparacao] = useState(null)
  const [showSeparar, setShowSeparar] = useState(false)
  const iframeRef = useRef(null)

  // Carregar config de relatórios
  useEffect(() => {
    if (!orgId) return
    mapasService.getConfig(orgId)
      .then(cfg => setReportConfig(cfg))
      .catch(() => {})
  }, [orgId])

  // Leilões
  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .catch(console.error)
      .finally(() => setLoadingL(false))
  }, [orgId])

  // Vendedores do leilão
  useEffect(() => {
    if (!orgId || !leilaoId) { setVendedores([]); return }
    setLoadingV(true)
    setVendedorId('')
    mapasService.getVendedores(orgId, leilaoId)
      .then(list => setVendedores(list || []))
      .catch(() => setVendedores([]))
      .finally(() => setLoadingV(false))
  }, [orgId, leilaoId])

  // Separação lembrada por leilão + vendedor (só com um vendedor selecionado)
  useEffect(() => {
    setSeparacao(leilaoId && vendedorId ? lerSep(leilaoId, vendedorId) : null)
  }, [leilaoId, vendedorId])

  // Modelo "Listagem": mesma tabela da Lista de Compradores, ordenada por vendedor
  useEffect(() => {
    if (modelo !== 'listagem' || !orgId || !leilaoId) { setDadosListagem(null); return }
    setLoadingListagem(true)
    mapasService.getListagemCompradoresDados(orgId, leilaoId, vendedorPor, { ordenar_por: 'vendedor', vendedor_id: vendedorId || undefined })
      .then(setDadosListagem)
      .catch(() => setDadosListagem(null))
      .finally(() => setLoadingListagem(false))
  }, [modelo, orgId, leilaoId, vendedorId, vendedorPor])

  // Lotes do leilão
  useEffect(() => {
    if (!orgId || !leilaoId) { setLotes([]); return }
    setLoading(true)
    lotesService.listarLotesPorLeilao(orgId, leilaoId)
      .then(data => setLotes(data || []))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [orgId, leilaoId])

  // Agrupar e filtrar
  const vendedoresAgrupados = useMemo(() => {
    const filtrados = vendedorId
      ? lotes.filter(l => String(l.vendedor_id) === vendedorId)
      : lotes

    const map = new Map()
    filtrados.forEach(lote => {
      const vid = lote.vendedor_id || 'sem-vendedor'
      if (!map.has(vid)) {
        map.set(vid, {
          vendedor: {
            id: vid,
            nome: lote.vendedor?.nome || '(sem vendedor)',
            fazenda: lote.fazenda?.nome || lote.vendedor?.fazenda || '',
            fazenda_cidade: lote.fazenda?.cidade || '',
            fazenda_uf: lote.fazenda?.estado || '',
          },
          lotes: [],
        })
      }
      map.get(vid).lotes.push(lote)
    })

    map.forEach(g => {
      g.lotes.sort((a, b) => {
        const na = parseInt(a.numero_lote) || 0
        const nb = parseInt(b.numero_lote) || 0
        return na !== nb ? na - nb : (a.numero_lote || '').localeCompare(b.numero_lote || '')
      })
    })

    let grupos = Array.from(map.values())
    if (vendedorId && separacao && modelo === 'detalhado' && grupos.length === 1) {
      const g = grupos[0]
      grupos = separarLotes(g.lotes, separacao).map((parte, i) => ({
        vendedor: { ...g.vendedor, id: `${g.vendedor.id}-${i}`,
          ...(separacao.criterio === 'fazenda' && parte.lotes[0] ? {
            fazenda: parte.lotes[0].fazenda?.nome || parte.lotes[0].fazenda_ficticia || '',
            fazenda_cidade: parte.lotes[0].fazenda?.cidade || '', fazenda_uf: parte.lotes[0].fazenda?.estado || '' } : {}) },
        lotes: parte.lotes,
        titulo: parte.titulo,
      }))
    }

    // Aplicar ordenação configurada
    const ordenacao = reportConfig?.lista_vendedores_ordenacao || 'nome_vendedor'
    switch (ordenacao) {
      case 'numero_lote':
        grupos.sort((a, b) => {
          const firstA = parseInt(a.lotes[0]?.numero_lote) || 0
          const firstB = parseInt(b.lotes[0]?.numero_lote) || 0
          return firstA - firstB
        })
        break
      case 'qtd_lotes':
        grupos.sort((a, b) => b.lotes.length - a.lotes.length)
        break
      case 'qtd_animais':
        grupos.sort((a, b) => {
          const totalA = a.lotes.reduce((s, l) => s + (parseInt(l.qtd) || 0), 0)
          const totalB = b.lotes.reduce((s, l) => s + (parseInt(l.qtd) || 0), 0)
          return totalB - totalA
        })
        break
      default: // 'nome_vendedor'
        grupos.sort((a, b) => a.vendedor.nome.localeCompare(b.vendedor.nome))
    }

    return grupos
  }, [lotes, vendedorId, reportConfig, separacao, modelo])

  const lotesVendedor = useMemo(() => (vendedorId ? lotes.filter(l => String(l.vendedor_id) === vendedorId) : []), [lotes, vendedorId])
  const aplicarSeparacao = (cfg) => { setSeparacao(cfg); salvarSep(leilaoId, vendedorId, cfg); setShowSeparar(false) }
  const leilaoAtual  = leiloes.find(l => String(l.id) === leilaoId)
  const totalGeral   = vendedoresAgrupados.reduce((s, g) => s + g.lotes.reduce((ss, l) => ss + (parseInt(l.qtd) || 0), 0), 0)
  const totalLotes   = vendedoresAgrupados.reduce((s, g) => s + g.lotes.length, 0)
  const dataEmissao  = new Date().toLocaleDateString('pt-BR')

  const { totalLotesMachos, totalQtdMachos, totalLotesFemeas, totalQtdFemeas } = useMemo(() => {
    let lm = 0, qm = 0, lf = 0, qf = 0
    vendedoresAgrupados.forEach(({ lotes: lotesVend }) => {
      lotesVend.forEach(l => {
        const sexo = (l.sexo || '').toLowerCase()
        if (sexo.startsWith('mac')) { lm++; qm += parseInt(l.qtd) || 0 }
        else if (sexo.startsWith('fêm') || sexo.startsWith('fem')) { lf++; qf += parseInt(l.qtd) || 0 }
      })
    })
    return { totalLotesMachos: lm, totalQtdMachos: qm, totalLotesFemeas: lf, totalQtdFemeas: qf }
  }, [vendedoresAgrupados])

  const logoOrgSrc = organizadorAtivo?.logo_url
    ? (organizadorAtivo.logo_url.startsWith('http')
        ? organizadorAtivo.logo_url
        : `${API_BASE}${organizadorAtivo.logo_url.startsWith('/') ? '' : '/'}${organizadorAtivo.logo_url}`)
    : null

  // Colunas ativas da config
  const DEFAULT_COLUNAS = ['lote', 'qtd', 'tipo', 'raca', 'sexo', 'idade', 'curral', 'peso', 'arroba']
  const colunasAtivas = reportConfig?.lista_vendedores_colunas_ativas?.length > 0
    ? reportConfig.lista_vendedores_colunas_ativas
    : DEFAULT_COLUNAS

  const handleImprimir = async () => {
    if (!orgId || !leilaoId) return
    setLoadingPdf(true)
    try {
      let blob
      if (modelo === 'lotes') {
        blob = await mapasService.getRelacaoVendedoresDetPdf(orgId, leilaoId)
      } else if (modelo === 'listagem') {
        blob = await mapasService.getListagemCompradoresPdf(orgId, leilaoId, vendedorPor, { ordenar_por: 'vendedor', vendedor_id: vendedorId || undefined })
      } else {
        blob = await mapasService.getListaVendedoresPdf(orgId, leilaoId, {
          vendedorId: vendedorId || undefined,
          separacao: separacao || undefined,
        })
      }
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) {
      alert('Erro ao gerar PDF: ' + (err.response?.statusText || err.message))
    } finally {
      setLoadingPdf(false)
    }
  }

  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador para visualizar a Lista de Vendedores.
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Título */}
      <div className="no-print flex items-center gap-2">
        <Users className="w-5 h-5 text-blue-600" />
        <h1 className="text-lg font-semibold text-gray-800">Lista de Vendedores</h1>
      </div>

      {/* ── Seletor ── */}
      <div className="no-print bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Leilão" value={leilaoId} onChange={setLeilaoId}
            disabled={loadingL} className="flex-[2] min-w-[200px]">
            {loadingL
              ? <option>Carregando...</option>
              : leiloes.map(l => (
                  <option key={l.id} value={String(l.id)}>
                    {l.nome} — {fmtDate(l.data_leilao)}
                  </option>
                ))
            }
          </Select>

          <Select label="Vendedor" value={vendedorId} onChange={setVendedorId}
            disabled={loadingV || !leilaoId} className="flex-[2] min-w-[200px]">
            <option value="">TODOS OS VENDEDORES</option>
            {vendedores.map(v => (
              <option key={v.id} value={String(v.id)}>{v.nome}</option>
            ))}
          </Select>

          <Select label="Modelo" value={modelo} onChange={setModelo} className="min-w-[140px]">
            <option value="detalhado">Detalhado</option>
            <option value="lotes">Por Lotes</option>
            <option value="listagem">Listagem</option>
          </Select>

          {modelo === 'listagem' && (
            <Select label="Vendedor exibido por" value={vendedorPor} onChange={setVendedorPor} className="min-w-[180px]">
              <option value="fazenda">Fazenda</option>
              <option value="nome">Nome do vendedor</option>
            </Select>
          )}

          <div className="flex items-end gap-2 ml-auto self-end">
            {vendedorId && modelo === 'detalhado' && (
              <button onClick={() => setShowSeparar(true)} disabled={loading || lotesVendedor.length === 0}
                title="Separar a lista por tipo de animal, sexo ou fazenda"
                className={`flex items-center gap-2 px-3 py-2 h-9 border rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${separacao ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}>
                <Split size={15} />
                {separacao ? 'Lista separada' : 'Separar lista'}
                {separacao && <XIcon size={13} className="hover:text-red-600" onClick={(e) => { e.stopPropagation(); aplicarSeparacao(null) }} />}
              </button>
            )}
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleImprimir}
              disabled={!leilaoId || loading || loadingPdf || (modelo === 'listagem' ? !dadosListagem?.lotes?.length : vendedoresAgrupados.length === 0)}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} />
              {loadingPdf ? 'Gerando PDF...' : 'Imprimir'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Preview ── */}
      {!leilaoId ? (
        <div className="no-print bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          Selecione um leilão para visualizar o relatório
        </div>
      ) : modelo === 'listagem' ? (
        loadingListagem || !dadosListagem ? (
          <div className="no-print bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <div className="print-page bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <div style={{ ...S.page, fontSize: 13 }}>
                <div style={S.hdr}>
                  <div style={{ minWidth: 140 }}>
                    {logoOrgSrc ? <img src={logoOrgSrc} alt="logo" style={S.hdrLogo} /> : <span style={{ fontWeight: 700 }}>{organizadorAtivo?.name || ''}</span>}
                  </div>
                  <div style={S.hdrCenter}>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>{leilaoAtual?.nome} — {fmtDate(leilaoAtual?.data_leilao)}</div>
                  </div>
                  <div style={{ minWidth: 140, textAlign: 'right' }}><img src="/assets/rematei.png" alt="Rematei" style={S.hdrLogo} /></div>
                </div>
                <table style={{ ...S.tbl, fontSize: 12 }}>
                  <thead><tr>
                    {['LT', 'QTDE', 'SEXO', 'IDADE', 'VLR UNIT', dadosListagem.col_valor_label || 'VLR TOTAL', 'VCTO', 'VENDEDOR', 'COMPRADOR'].map(h => <th key={h} style={S.th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {dadosListagem.lotes.map((l, i) => (
                      <tr key={i}>
                        <td style={{ ...S.tdC, fontWeight: 700 }}>{l.numero_lote}</td>
                        <td style={S.tdC}>{l.qtd}</td>
                        <td style={S.tdC}>{l.sexo === 'Macho' ? 'M' : l.sexo === 'Fêmea' || l.sexo === 'Femea' ? 'F' : l.sexo || '—'}</td>
                        <td style={S.tdC}>{l.idade || '—'}</td>
                        <td style={S.tdR}>{brl(l.vlr_unit)}</td>
                        <td style={S.tdR}>{brl(l.vlr_total)}</td>
                        <td style={S.tdC}>{fmtDataIso(l.vcto) || '—'}</td>
                        <td style={{ ...S.td, fontWeight: 600 }}>{l.vendedor}</td>
                        <td style={S.td}>{l.comprador}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ fontSize: 11, fontWeight: 700, textAlign: 'right', marginTop: 6 }}>
                  Lotes: {dadosListagem.lotes.length} | Animais: {dadosListagem.lotes.reduce((s, l) => s + l.qtd, 0)} | Total: {brl(dadosListagem.lotes.reduce((s, l) => s + l.vlr_total, 0))}
                </div>
                <div style={S.footer}>www.rematei.com.br</div>
              </div>
            </div>
          </div>
        )
      ) : loading ? (
        <div className="no-print bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : vendedoresAgrupados.length === 0 ? (
        <div className="no-print bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          Nenhum lote encontrado para os filtros selecionados
        </div>
      ) : (
        <div className="print-page bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div style={S.page}>
            {/* Cabeçalho com logos */}
            <div style={S.hdr}>
              <div style={{ minWidth: 140 }}>
                {logoOrgSrc
                  ? <img src={logoOrgSrc} alt="logo" style={S.hdrLogo} />
                  : <span style={{ fontWeight: 700, fontSize: 14 }}>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</span>
                }
              </div>
              <div style={S.hdrCenter}>
                <div style={S.hdrT1}>Lista de Vendedores</div>
                {leilaoAtual && (
                  <div style={S.hdrT2}>
                    {leilaoAtual.nome} — {fmtDate(leilaoAtual.data_leilao)}
                  </div>
                )}
                {vendedorId && vendedores.find(v => String(v.id) === vendedorId) && (
                  <div style={{ ...S.hdrT3, marginTop: 2 }}>
                    Vendedor: {vendedores.find(v => String(v.id) === vendedorId)?.nome}
                  </div>
                )}
                <div style={S.hdrT3}>Emitido em {dataEmissao}</div>
              </div>
              <div style={{ minWidth: 140, textAlign: 'right' }}>
                <img src="/assets/rematei.png" alt="Rematei" style={S.hdrLogo} />
              </div>
            </div>

            {/* Conteúdo baseado no modelo */}
            {modelo === 'detalhado' ? (
              <>
                {/* Blocos por vendedor */}
                {vendedoresAgrupados.map(({ vendedor, lotes: lotesVend, titulo }) => (
                  <TabelaVendedor key={vendedor.id} vendedor={vendedor} lotes={lotesVend} colunasAtivas={colunasAtivas} titulo={titulo} />
                ))}

                {/* Total geral — tabela igual ao catálogo */}
                <div style={{ marginTop: 10 }}>
                  <table style={S.totTbl}>
                    <thead>
                      <tr>
                        <th style={{ ...S.totThL, width: '25%' }}>ESPÉCIE</th>
                        <th style={{ ...S.totThC, width: '19%' }}>QTD. LOTES<br />DE MACHOS</th>
                        <th style={{ ...S.totThC, width: '19%' }}>QTD. DE<br />MACHOS</th>
                        <th style={{ ...S.totThC, width: '19%' }}>QTD. LOTES<br />DE FÊMEAS</th>
                        <th style={{ ...S.totThC, width: '18%' }}>QTD. DE<br />FÊMEAS</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={S.totTd}>BOVINOS</td>
                        <td style={S.totTdC}>{totalLotesMachos}</td>
                        <td style={S.totTdC}>{totalQtdMachos}</td>
                        <td style={S.totTdC}>{totalLotesFemeas}</td>
                        <td style={S.totTdC}>{totalQtdFemeas}</td>
                      </tr>
                      <tr>
                        <td style={S.totTotRow} colSpan={5}>
                          <strong>TOTAL DE LOTES: {totalLotes}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;TOTAL DE MACHOS: {totalQtdMachos}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;TOTAL DE FÊMEAS: {totalQtdFemeas}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;TOTAL GERAL: {totalGeral}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <>
                {/* Modelo por Lotes */}
                <table style={S.tbl}>
                  <thead><tr>
                    <th style={{ ...S.th, width: '6%' }}>Cód.</th>
                    <th style={{ ...S.th, width: '40%' }}>Vendedor</th>
                    <th style={{ ...S.th, width: '14%' }}>{labelCurral}</th>
                    <th style={{ ...S.thC, width: '20%' }}>Qtd. Animais</th>
                    <th style={{ ...S.thC, width: '20%' }}>Qtd. Lotes</th>
                  </tr></thead>
                  <tbody>
                    {vendedoresAgrupados.map(({ vendedor, lotes: lotesVend }) => {
                      const lotesNums = lotesVend.map(l => l.numero_lote).join(', ')
                      const currais = [...new Set(lotesVend.map(l => l.curral).filter(Boolean))].join(', ')
                      const qtdAnimais = lotesVend.reduce((s, l) => s + (parseInt(l.qtd) || 0), 0)
                      return (
                        <tr key={vendedor.id}>
                          <td style={S.td}>{vendedor.id}</td>
                          <td style={S.td}>
                            <div style={{ fontWeight: 600 }}>{vendedor.nome}</div>
                            <div style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Lotes: {lotesNums}</div>
                          </td>
                          <td style={S.td}>{currais || '—'}</td>
                          <td style={S.tdC}>{qtdAnimais}</td>
                          <td style={S.tdC}>{lotesVend.length}</td>
                        </tr>
                      )
                    })}
                    <tr style={S.totRow}>
                      <td style={{ ...S.td, borderTop: '2px solid #1f2937' }}></td>
                      <td style={{ ...S.td, borderTop: '2px solid #1f2937' }}><strong>TOTAL</strong></td>
                      <td style={{ ...S.td, borderTop: '2px solid #1f2937' }}></td>
                      <td style={{ ...S.tdC, borderTop: '2px solid #1f2937' }}><strong>{totalGeral}</strong></td>
                      <td style={{ ...S.tdC, borderTop: '2px solid #1f2937' }}><strong>{totalLotes}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            <div style={S.footer}>www.rematei.com.br</div>
          </div>
        </div>
      )}

      {/* CSS de impressão */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
          .print-page {
            border: none !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
        }
      `}</style>

      {showSeparar && (
        <SepararFaturaModal
          modoLista
          opcoes={opcoesSeparacao(lotesVendedor)}
          fluxo=""
          atual={separacao}
          onClose={() => setShowSeparar(false)}
          onApply={aplicarSeparacao}
        />
      )}

      {/* Modal PDF */}
      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">Lista de Vendedores — {leilaoAtual?.nome}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => downloadPdf(pdfUrl, `Lista de Vendedores - ${leilaoAtual?.nome || ''}.pdf`)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">
                  Salvar PDF
                </button>
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer size={15} /> Imprimir
                </button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Lista de Vendedores" style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
